import React, { useState, useRef, TouchEventHandler } from 'react';
import styled from '@emotion/styled';
import Icon from '~ComponentsImproved/Icon';
import { useDispatch } from '~Hooks';
import { addToast } from '~actions';

const Wrapper = styled.button<{
  width: string;
  minHeight: string;
  bg: string;
  textColor: string;
  boxShadow: string;
  style: React.CSSProperties | undefined;
  hover: boolean;
  disabled: boolean;
}>`
  font-size: 14px;
  font-weight: 600;
  font-family: 'Eina01', 'HelveticaNeue', Helvetica, Arial, sans-serif;
  letter-spacing: 0.24px;
  line-height: 16px;
  text-align: center;
  background: ${({ bg }) => bg};
  color: ${({ textColor }) => textColor};
  min-height: ${({ minHeight }) => minHeight};
  width: ${({ width }) => width};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  box-shadow: ${({ boxShadow }) => boxShadow};
  display: flex;
  flex-flow: row wrap;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  padding: 6px;
  outline: none;
  border: none;
  transform: ${({ hover }) => (hover ? 'scale(0.97)' : '1')};
  transition: transform 0.1s ease-out, opacity 0.1s ease-in-out;

  div + span {
    margin-left: 10px;
  }
`;

export type ButtonStyleType = 'Clear' | 'Confirm' | 'Warning' | 'Dark' | 'Default' | 'OK' | 'Gray';

export interface Props {
  text?: React.ReactNode;
  onTouchStart?: (e?: React.TouchEvent<any>) => void;
  onTouchEnd?: (e?: React.TouchEvent<any>) => void;
  onLongPress?: () => void;
  disabled?: boolean;
  disabledToast?: string;
  width?: string;
  height?: string;
  style?: React.CSSProperties;
  styleType?: ButtonStyleType;
  icon?: string;
  children?: React.ReactNode;
}

const Button: React.FunctionComponent<Props> = ({
  text,
  onLongPress,
  onTouchStart,
  onTouchEnd,
  disabled = false,
  disabledToast,
  width = '100%',
  height = '56px',
  styleType,
  style,
  icon,
  children,
}) => {
  const [hover, setHover] = useState(false);
  const [touching, setTouching] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const isStorybook = !!process.env.STORYBOOK_MODE;
  const dispatch = useDispatch();

  let bg = '#ffffff';
  let textColor = 'var(--colorBlueGray1200)';
  let disabledColor = 'var(--colorBlueGray400)';
  let boxShadow = 'none';

  switch (styleType) {
    case 'Clear':
      textColor = 'var(--colorUIRed)';
      break;

    case 'Confirm':
      bg = 'linear-gradient(-180deg, rgb(32, 153, 70) 0%, rgb(23, 132, 69) 100%)';
      textColor = '#ffffff';
      disabledColor = '#fff';
      break;

    case 'OK':
      bg = disabled
        ? 'linear-gradient(180deg, #8B8F93 0.05%, #666B71 100%)'
        : 'linear-gradient(180deg, #1642BE 0.69%, #173792 100%)';
      textColor = '#ffffff';
      disabledColor = '#fff';
      break;

    case 'Warning':
      bg = 'var(--colorUIRed)';
      textColor = '#ffffff';
      disabledColor = '#fff';
      break;

    case 'Dark':
      bg = 'var(--colorBlueGray1300)';
      textColor = '#ffffff';
      disabledColor = 'var(--colorBlueGray700)';
      boxShadow = '4px 4px 10px rgb(0 0 0 / 21%)';
      break;

    case 'Gray':
      bg = 'var(--colorBlueGray1000)';
      textColor = '#ffffff';
      disabledColor = 'var(--colorBlueGray700)';
      break;

    default:
      break;
  }

  const handleLongPress = () => {
    setTouching(true);

    timeoutRef.current = setTimeout(() => {
      onLongPress && onLongPress();
      setTouching(false);
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (touching) {
      clearTimeout(timeoutRef.current!);
      setTouching(false);
    }
  };

  return (
    <Wrapper
      onTouchStart={(e) => {
        if (disabled) {
          return;
        }

        setHover(true);

        if (onTouchStart) {
          onTouchStart(e);
        }

        if (onLongPress) {
          handleLongPress();
        }
      }}
      onTouchEnd={(e) => {
        setHover(false);

        if (disabled) {
          if (disabledToast) {
            dispatch(addToast(disabledToast, true));
          }
          return;
        }

        if (onTouchEnd) {
          onTouchEnd(e);
        }

        if (onLongPress) {
          handleLongPressEnd();
        }
      }}
      onClick={(e) => {
        if (!isStorybook) {
          return;
        }
        e.stopPropagation();

        onTouchStart && onTouchStart();
        onTouchEnd && onTouchEnd();
      }}
      hover={hover}
      width={width}
      minHeight={height}
      bg={bg}
      disabled={disabled}
      boxShadow={boxShadow}
      textColor={disabled ? disabledColor : textColor}
      style={style}>
      {icon && <Icon src={icon} color={disabled ? disabledColor : textColor} />}
      {text && <span>{text}</span>}
      {children && <span>{children}</span>}
    </Wrapper>
  );
};

export default React.memo(Button);
